import React, {CSSProperties, FC, PropsWithChildren} from 'react';
import {Popper} from 'react-popper';

import {Anchor, buildReferenceObject, ReferenceObject} from './repositionHelpers';

/*
 * Props.
 */

interface RepositionOverlayProps {
  /** Ref to the anchor, or bounds provider. */
  anchor: Anchor;
  /** Whether to allow pointer events. */
  isInteractive?: boolean;
}

/*
 * Component.
 */

/** Position content over an existing element. */
export const RepositionOverlay: FC<PropsWithChildren<RepositionOverlayProps>> = ({
  anchor,
  children,
  isInteractive,
}) => {
  const referenceObject = buildReferenceObject(anchor);
  return (
    <Popper
      referenceElement={referenceObject}
      placement="top-start"
      modifiers={[
        {name: 'preventOverflow', enabled: false},
        {
          name: 'computeStyles',
          options: {
            gpuAcceleration: false,
          },
        },
        {
          name: 'offset',
          // https://github.com/popperjs/popper-core/issues/965#issuecomment-577247660
          options: {
            offset: ({placement, popper}) => [
              0,
              -(placement.includes('left') || placement.includes('right') ? popper.width : popper.height),
            ],
          },
        },
      ]}
    >
      {({ref, style}) => (
        <div
          ref={ref}
          style={{
            ...style,
            pointerEvents: isInteractive ? 'auto' : 'none',
            ...computeOverlaySize(referenceObject),
          }}
          data-placement="top-start"
        >
          {children}
        </div>
      )}
    </Popper>
  );
};

function computeOverlaySize(referenceObject: ReferenceObject): CSSProperties {
  const {width, height} = referenceObject.getBoundingClientRect();
  return {width, height};
}
